import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import api from "../../utils/axios.config";
import { toast } from "react-hot-toast";
import Loading from "../Shared/Loading";
import districts from "../../data/district.json";
import upazilas from "../../data/upazila.json";
import {
  User,
  Phone,
  MapPin,
  Upload,
  Heart,
  UserPlus,
  CheckCircle,
  AlertCircle,
  Droplet,
  X,
} from "lucide-react";

const bloodGroups = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const AddBloodDonor = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedUpazila, setSelectedUpazila] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [donorData, setDonorData] = useState(null);

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm();

  // Function to get district name by ID
  const getDistrictById = (id) => {
    const district = districts.find((district) => district.id === id);
    return district ? district.bn_name : null;
  };

  // Function to get upazila name by ID
  const getUpazilaById = (id) => {
    const upazila = upazilas.find((upazila) => upazila.id === id);
    return upazila ? upazila.bn_name : null;
  };


  // Fetch donor data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      fetchDonorData();
    }
  }, [id, isEditMode]);

  const fetchDonorData = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/blood-donor/${id}`);
      const donor = response.data.data;
      setDonorData(donor);
      
      // Pre-fill form with existing data
      setValue("name", donor.name);
      setValue("bloodGroup", donor.bloodGroup);
      setValue("mobile", donor.mobile);
      // Format date for input field (YYYY-MM-DD)
      if (donor.lastDonationDate) {
        const date = new Date(donor.lastDonationDate);
        const formattedDate = date.toISOString().split('T')[0];
        setValue("lastDonationDate", formattedDate);
      }
      
      // Find and set district/upazila IDs
      const districtId = districts.find(d => d.bn_name === donor.district)?.id || "";
      const upazilaId = upazilas.find(u => u.bn_name === donor.upazila)?.id || "";
      
      setValue("district", districtId);
      setValue("upazila", upazilaId);
      setSelectedDistrict(districtId);
      setSelectedUpazila(upazilaId);
      
      // Set image preview if exists
      if (donor.profileImage) {
        setImagePreview(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${donor.profileImage}`);
      }
    } catch (error) {
      toast.error("Error fetching donor data: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);

    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("bloodGroup", data.bloodGroup);
    formData.append("mobile", data.mobile);
    formData.append("district", getDistrictById(data.district));
    formData.append("upazila", getUpazilaById(data.upazila));
    if (data.lastDonationDate) {
      formData.append("lastDonationDate", data.lastDonationDate);
    }
    if (selectedImage) {
      formData.append("profileImage", selectedImage);
    }

    try {
      let response;
      if (isEditMode) {
        response = await api.put(`/blood-donor/${id}`, formData);
      } else {
        response = await api.post("/blood-donor/create", formData);
      }
      
      if (response.data.success) {
        toast.success(
          response.data.message
            ? response.data.message
            : isEditMode ? "Blood donor updated successfully" : "Successfully inserted blood donor"
        );
        if (!isEditMode) {
          reset();
          setSelectedImage(null);
          setImagePreview(null);
        }
      } else {
        toast.error(response.data.error);
      }
    } catch (error) {
      toast.error(error.response?.data?.error ?? error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDistrictChange = (e) => {
    setSelectedDistrict(e.target.value);
    setSelectedUpazila("");
  };

  const handleUpazilaChange = (e) => {
    setSelectedUpazila(e.target.value);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    // Reset the file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = "";
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-pink-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
            <Heart className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-3">
            {isEditMode ? "রক্তদাতার তথ্য সম্পাদনা" : "NNS ব্লাড টিম (একটি NNS এর পরিচালনাধীন স্বেচ্ছাসেবী উইং)"}
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {isEditMode ? "রক্তদাতার তথ্য আপডেট করুন" : "আপনাকে স্বাগতম, NNS ব্লাড টিমে রক্তদাতা হিসেবে নিবন্ধন সম্পন্ন করুন"}
          </p>
        </div>

        {/* Form Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <div className="space-y-8">
            {/* Personal Information Section */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-red-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800">
                  ব্যক্তিগত তথ্য
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Name Field */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    পূর্ণ নাম *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...register("name", { required: "নাম প্রয়োজন" })}
                      type="text"
                      placeholder="আপনার পূর্ণ নাম লিখুন"
                      className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors ${
                        errors.name
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300"
                      }`}
                    />
                  </div>
                  {errors.name && (
                    <div className="flex items-center gap-1 text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {errors.name.message}
                    </div>
                  )}
                </div>

                {/* Blood Group Field */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    রক্তের গ্রুপ *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Droplet className="h-5 w-5 text-red-400" />
                    </div>
                    <select
                      {...register("bloodGroup", {
                        required: "রক্তের গ্রুপ প্রয়োজন",
                      })}
                      className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors ${
                        errors.bloodGroup
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300"
                      }`}
                    >
                      <option value="">রক্তের গ্রুপ নির্বাচন করুন</option>
                      {bloodGroups.map((group) => (
                        <option key={group} value={group}>
                          {group}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.bloodGroup && (
                    <div className="flex items-center gap-1 text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {errors.bloodGroup.message}
                    </div>
                  )}
                </div>

                {/* Mobile Field */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    মোবাইল নম্বর *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...register("mobile", {
                        required: "মোবাইল নম্বর প্রয়োজন",
                      })}
                      type="text"
                      placeholder="আপনার মোবাইল নম্বর"
                      className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors ${
                        errors.mobile
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300"
                      }`}
                    />
                  </div>
                  {errors.mobile && (
                    <div className="flex items-center gap-1 text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {errors.mobile.message}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Address Information Section */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-red-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800">
                  ঠিকানার তথ্য
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* District Field */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    জেলা *
                  </label>
                  <select
                    {...register("district", { required: "জেলা প্রয়োজন" })}
                    className={`w-full px-3 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors ${
                      errors.district
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                    onChange={handleDistrictChange}
                  >
                    <option value="">জেলা নির্বাচন করুন</option>
                    {districts.map((district) => (
                      <option key={district.id} value={district.id}>
                        {district.bn_name}
                      </option>
                    ))}
                  </select>
                  {errors.district && (
                    <div className="flex items-center gap-1 text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {errors.district.message}
                    </div>
                  )}
                </div>

                {/* Upazila Field */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    উপজেলা *
                  </label>
                  <select
                    {...register("upazila", { required: "উপজেলা প্রয়োজন" })}
                    className={`w-full px-3 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors ${
                      errors.upazila
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    } ${
                      !selectedDistrict ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    onChange={handleUpazilaChange}
                    disabled={!selectedDistrict}
                  >
                    <option value="">উপজেলা নির্বাচন করুন</option>
                    {upazilas
                      .filter(
                        (upazila) => upazila.district_id === selectedDistrict
                      )
                      .map((upazila) => (
                        <option key={upazila.id} value={upazila.id}>
                          {upazila.bn_name}
                        </option>
                      ))}
                  </select>
                  {errors.upazila && (
                    <div className="flex items-center gap-1 text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {errors.upazila.message}
                    </div>
                  )}
                </div>

                {/* Last Donation Date Field */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    সর্বশেষ রক্তদানের তারিখ
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Droplet className="h-5 w-5 text-red-400" />
                    </div>
                    <input
                      {...register("lastDonationDate")}
                      type="date"
                      className="w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors border-gray-300"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Image Section */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <Upload className="w-4 h-4 text-red-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800">
                  প্রোফাইল ছবি
                </h2>
              </div>

              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  আপনার ছবি আপলোড করুন
                </label>

                {/* Image Preview */}
                {imagePreview ? (
                  <div className="relative inline-block">
                    <div className="relative w-32 h-32 rounded-lg overflow-hidden border-2 border-gray-300 shadow-md">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                        <button
                          type="button"
                          onClick={removeImage}
                          className="w-8 h-8 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      ছবি পরিবর্তন করতে নিচে ক্লিক করুন
                    </p>
                  </div>
                ) : null}

                {/* File Upload Area */}
                <div className="flex items-center justify-center w-full">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 mb-4 text-gray-500" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">ক্লিক করুন</span> অথবা
                        ছবি টেনে আনুন
                      </p>
                      <p className="text-xs text-gray-500">
                        PNG, JPG বা JPEG (MAX. 5MB)
                      </p>
                    </div>
                    <input
                      type="file"
                      onChange={handleImageChange}
                      className="hidden"
                      accept="image/*"
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-gray-200">
              <button
                type="submit"
                onClick={handleSubmit(onSubmit)}
                className="w-full md:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white font-medium rounded-lg hover:from-red-700 hover:to-red-800 focus:ring-4 focus:ring-red-300 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <UserPlus className="w-5 h-5" />
                {isEditMode ? "তথ্য আপডেট করুন" : "নিবন্ধন সম্পন্ন করুন"}
              </button>
            </div>
          </div>
        </div>

        {/* Footer Message */}
        <div className="text-center mt-8 p-6 bg-white rounded-lg shadow-md">
          <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">{isEditMode ? "তথ্য সম্পাদনা সম্পন্ন" : "আপনার অবদান জীবন বাঁচাবে"}</span>
          </div>
          <p className="text-gray-600 text-sm">
            {isEditMode ? "রক্তদাতার তথ্য সফলভাবে আপডেট করা হয়েছে।" : "রক্তদানে কোনো ক্ষতি নেই, বরং এটি আপনার স্বাস্থ্যের জন্য উপকারী এবং অন্যের জীবন রক্ষা করে।"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AddBloodDonor;
